import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLayerGroup,
    faFileContract,
    faFileInvoiceDollar,
    faFileLines,
    faScaleBalanced,
    faLock,
    faTh,
    faList,
    faMagnifyingGlass,
    faXmark,
    faPenToSquare,
    faEye,
    faChevronRight,
    faBuilding,
    faCircleCheck,
    faStamp,
    faTriangleExclamation,
    faInbox,
    faFilePdf,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import Footer from '../../components/ui/Footer';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSButton from '../../components/ui/HMSButton';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSEmptyState from '../../components/ui/HMSEmptyState';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import PdfPreviewModal from '../../components/shared/PdfPreviewModal';
import { useFlashMessage } from '../../components/FlashMessage';
import withAuth from '../../utils/withAuth';
import templateHubService from '../../services/templateHubService';
import ApprovalsTab from './ApprovalsTab';
import BlockEditorModal from './BlockEditorModal';
import './TemplateHeadquarters.css';

const MODULE_ICONS = {
    branding: faBuilding,
    enquiries: faFileLines,
    deals: faFileContract,
    contracts: faScaleBalanced,
    invoicing: faFileInvoiceDollar,
    kyc: faLock,
    other: faLayerGroup,
};

// Sensible category ordering for the block groups within a template drawer.
const CATEGORY_ORDER = ['Legal / Contract Terms', 'Branding / Entity', 'Standard Copy', 'Label'];

// Only these document types have a backend preview renderer. Everything else
// (soa_group / sanctions_report / cargo_deal — no renderer yet) gets no preview
// button so we never render a broken control.
const PREVIEWABLE_DOC_TYPES = new Set([
    'customer_confirmation', 'supplier_nomination', 'cargo_deal',
    'solids_sale_contract', 'solids_purchase_contract',
    'invoice', 'proforma', 'credit_note', 'debit_note', 'soa', 'soa_group', 'sanctions_report',
]);
const DOC_TYPE_LABEL = {
    customer_confirmation: 'Customer Confirmation',
    supplier_nomination: 'Supplier Nomination',
    cargo_deal: 'Cargo Deal / Offer Recap',
    solids_sale_contract: 'Cargo Sales Contract',
    solids_purchase_contract: 'Cargo Purchase Contract',
    invoice: 'Outgoing Invoice',
    proforma: 'Proforma Invoice',
    credit_note: 'Credit Note',
    debit_note: 'Debit Note',
    soa: 'Statement of Accounts',
    soa_group: 'Statement of Accounts (Group)',
    sanctions_report: 'Sanctions Screening Report',
};

// Invoice-family types accept a Template (solids_eng|solids_de|fuels_eng) +
// number Format (US|EU) variant, chosen in the drawer before previewing.
const INVOICE_FAMILY_DOC_TYPES = new Set(['invoice', 'proforma', 'credit_note', 'debit_note']);
const DEFAULT_INVOICE_VARIANT = { template_type: 'solids_eng', number_format: 'US' };

// Deals module splits into two labelled sub-sections in its tab.
const DEALS_SUBGROUPS = [
    { label: 'Bunkers', docTypes: ['customer_confirmation', 'supplier_nomination'] },
    { label: 'Cargo', docTypes: ['cargo_deal', 'solids_sale_contract', 'solids_purchase_contract'] },
];

// Resolve the preview descriptor for a cabinet card (or null when unsupported).
// - CC / SN / invoice-family / SOA templates preview themselves. Invoice-family
//   types also forward the chosen Template + Format variant.
// - Branding cards preview this entity's details on the shared letterhead.
const getTemplatePreview = (template, variant = DEFAULT_INVOICE_VARIANT) => {
    if (PREVIEWABLE_DOC_TYPES.has(template.doc_type)) {
        const label = DOC_TYPE_LABEL[template.doc_type] || 'Document';
        const isInvoiceFamily = INVOICE_FAMILY_DOC_TYPES.has(template.doc_type);
        return {
            key: `tpl-${template.id}`,
            label: 'Preview',
            caption: '',
            fileName: `${label} preview.pdf`,
            run: () => templateHubService.getPreviewObjectUrl({
                doc_type: template.doc_type,
                entity_id: template.hms_entity,
                ...(isInvoiceFamily ? {
                    template_type: variant.template_type,
                    number_format: variant.number_format,
                } : {}),
            }),
        };
    }
    if (template.module === 'branding' && template.doc_type === 'entity_branding') {
        return {
            key: `tpl-${template.id}`,
            label: 'Preview letterhead',
            caption: "How this entity's details appear on the header & footer of every document.",
            fileName: `${template.hms_entity_name || 'Entity'} letterhead.pdf`,
            run: () => templateHubService.getPreviewObjectUrl({
                doc_type: 'letterhead',
                entity_id: template.hms_entity,
            }),
        };
    }
    return null;
};

// A block's version state as an HMSBadge tone. "No content" is the absence of
// a version rather than a document state, so it takes the primitive's neutral
// fallback (no `status`) instead of borrowing a tone that means something else.
const blockStatus = (block) => {
    if (block.pending_version) {
        return { status: 'pending', label: 'Pending approval' };
    }
    if (block.active_version_number != null) {
        return { status: 'approved', label: `Live · v${block.active_version_number}` };
    }
    return { status: undefined, label: 'No content' };
};

const TemplateHeadquarters = () => {
    const { showError } = useFlashMessage();
    const [templates, setTemplates] = useState([]);
    const [allBlocks, setAllBlocks] = useState([]);
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Single tab-bar selection: 'all', a module value (e.g. 'branding' /
    // 'invoicing'), or 'approvals'. The category tabs + Approvals ARE the tabs.
    const [activeTab, setActiveTab] = useState('all');
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid');

    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editingBlock, setEditingBlock] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    // Invoice-family variant chosen in the drawer (Template + number Format).
    const [invoiceVariant, setInvoiceVariant] = useState(DEFAULT_INVOICE_VARIANT);

    // Single reusable PDF-preview host (used by cards, drawer, editor & approvals).
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewTitle, setPreviewTitle] = useState('');
    const [previewLoading, setPreviewLoading] = useState(''); // key of the button in flight
    // Latest object URL, so unmount can revoke a still-open blob (belt & braces).
    const previewUrlRef = useRef(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Reset the variant selectors each time a different template drawer opens.
    useEffect(() => {
        setInvoiceVariant(DEFAULT_INVOICE_VARIANT);
    }, [selectedTemplate]);

    useEffect(() => () => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    }, []);

    // Awaits an object-URL factory, then opens the preview modal. `key` scopes
    // the loading state to the button that started it; errors surface via Flash.
    const openPreview = useCallback(async (run, title, key) => {
        setPreviewLoading(key);
        try {
            const url = await run();
            previewUrlRef.current = url;
            setPreviewUrl(url);
            setPreviewTitle(title);
            setPreviewOpen(true);
        } catch (err) {
            showError(await templateHubService.parseBlobError(err));
        } finally {
            setPreviewLoading('');
        }
    }, [showError]);

    const closePreview = useCallback(() => {
        setPreviewOpen(false);
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewUrl(null);
        setPreviewTitle('');
    }, []);

    const loadData = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError('');
        try {
            const [templatesData, blocksData, pendingData] = await Promise.all([
                templateHubService.getTemplates({}),
                templateHubService.getBlocks({}),
                templateHubService.getPendingApprovals(),
            ]);
            setTemplates(templatesData);
            setAllBlocks(blocksData);
            setPending(pendingData);
            if (pendingData.length === 0) {
                setActiveTab((tab) => (tab === 'approvals' ? 'all' : tab));
            }
        } catch (err) {
            setError('Could not load Template Headquarters. Please try again.');
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const blocksById = useMemo(() => {
        const map = {};
        allBlocks.forEach((b) => { map[b.id] = b; });
        return map;
    }, [allBlocks]);

    // Category tabs are derived dynamically from the loaded templates: each
    // distinct module becomes a tab ({ value: module, label: module_display }).
    const moduleOptions = useMemo(() => {
        const seen = new Map();
        templates.forEach((t) => {
            if (!seen.has(t.module)) seen.set(t.module, t.module_display);
        });
        return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
    }, [templates]);

    // Template count per module (drives the per-tab badge). Search-independent so
    // the badges stay stable while the user types.
    const moduleCounts = useMemo(() => {
        const counts = {};
        templates.forEach((t) => { counts[t.module] = (counts[t.module] || 0) + 1; });
        return counts;
    }, [templates]);

    const filteredTemplates = useMemo(() => {
        const q = search.trim().toLowerCase();
        return templates.filter((t) => {
            // 'all' shows everything; a module tab shows only that module.
            // ('approvals' never reaches the grid — it renders ApprovalsTab.)
            if (activeTab !== 'all' && activeTab !== 'approvals' && t.module !== activeTab) return false;
            if (!q) return true;
            return (
                (t.name || '').toLowerCase().includes(q)
                || (t.doc_type || '').toLowerCase().includes(q)
                || (t.hms_entity_name || '').toLowerCase().includes(q)
            );
        });
    }, [templates, activeTab, search]);

    // Blocks for the open drawer, grouped by category_display in a stable order.
    const groupedBlocks = useMemo(() => {
        if (!selectedTemplate) return [];
        const mine = allBlocks.filter((b) => b.template === selectedTemplate.id);
        const groups = {};
        mine.forEach((b) => {
            const key = b.category_display || 'Other';
            (groups[key] = groups[key] || []).push(b);
        });
        return Object.entries(groups).sort((a, b) => {
            const ai = CATEGORY_ORDER.indexOf(a[0]);
            const bi = CATEGORY_ORDER.indexOf(b[0]);
            return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a[0].localeCompare(b[0]);
        });
    }, [selectedTemplate, allBlocks]);

    const showApprovalsTab = pending.length > 0;

    // Header tab tray: "All", a tab per template module (derived from
    // moduleOptions), and a trailing "Approvals" tab when there is pending
    // work. The counts the labels used to spell out ride as pill badges;
    // showZero keeps an empty module showing 0 the way "(0)" did.
    const thqTabs = [
        { key: 'all', label: 'All', icon: faLayerGroup, count: templates.length, showZero: true },
        ...moduleOptions.map((m) => ({
            key: m.value,
            label: m.label,
            icon: MODULE_ICONS[m.value] || faLayerGroup,
            count: moduleCounts[m.value] || 0,
            showZero: true,
        })),
        ...(showApprovalsTab
            ? [{ key: 'approvals', label: 'Approvals', icon: faInbox, count: pending.length, showZero: true }]
            : []),
    ];

    const openMaster = (template) => {
        if (!template.master_file) return;
        const url = template.master_file;
        const name = url.split('/').pop() || 'master-file';
        if (url.toLowerCase().split('?')[0].endsWith('.pdf')) {
            setPreviewFile({ url, name });
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const renderTemplateCard = (template) => {
        const icon = MODULE_ICONS[template.module] || faLayerGroup;
        const preview = getTemplatePreview(template);
        const previewBusy = previewLoading === preview?.key;
        // The card is the open control; it is a div-with-role (not a <button>) so
        // it can legally host the nested Preview action button.
        return (
            <div
                role="button"
                tabIndex={0}
                key={template.id}
                className={`thq-tpl-card ${template.is_active ? '' : 'thq-tpl-card-inactive'}`}
                onClick={() => setSelectedTemplate(template)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedTemplate(template);
                    }
                }}
            >
                <div className="thq-tpl-card-top">
                    <div className="thq-tpl-icon"><FontAwesomeIcon icon={icon} /></div>
                    <span className="thq-module-chip">{template.module_display}</span>
                    {!template.is_active && <HMSBadge>Inactive</HMSBadge>}
                </div>
                <div className="thq-tpl-name">{template.name}</div>
                <code className="thq-tpl-doctype">{template.doc_type}</code>
                {template.description ? (
                    <p className="thq-tpl-desc">{template.description}</p>
                ) : null}
                <div className="thq-tpl-foot">
                    <span className="thq-tpl-entity">
                        <FontAwesomeIcon icon={faBuilding} />
                        {template.hms_entity_name || 'All entities'}
                    </span>
                    <span className="thq-tpl-blocks">
                        {template.block_count} block{template.block_count === 1 ? '' : 's'}
                        <FontAwesomeIcon icon={faChevronRight} className="thq-tpl-chevron" />
                    </span>
                </div>
                {preview ? (
                    <div className="thq-tpl-preview-row">
                        <HMSButton
                            variant="ghost"
                            size="compact"
                            onClick={(e) => {
                                e.stopPropagation();
                                openPreview(preview.run, preview.fileName, preview.key);
                            }}
                            disabled={Boolean(previewLoading)}
                            title={preview.caption || undefined}
                        >
                            <FontAwesomeIcon icon={previewBusy ? faSpinner : faFilePdf} spin={previewBusy} />
                            {previewBusy ? 'Preparing…' : preview.label}
                        </HMSButton>
                        {preview.caption ? (
                            <span className="thq-tpl-preview-hint">{preview.caption}</span>
                        ) : null}
                    </div>
                ) : null}
            </div>
        );
    };

    // A flat grid/list of cards, honouring the current view mode.
    const renderTemplateCollection = (list) => (
        <div className={viewMode === 'grid' ? 'thq-tpl-grid' : 'thq-tpl-list'}>
            {list.map(renderTemplateCard)}
        </div>
    );

    // Deals tab: split cards into Bunkers / Cargo sub-sections. Anything not in a
    // known sub-group renders flat after them so no card is silently dropped.
    const renderDealsGroups = (list) => {
        const grouped = new Set();
        return (
            <div className="thq-subsection-stack">
                {DEALS_SUBGROUPS.map(({ label, docTypes }) => {
                    const cards = list.filter((t) => docTypes.includes(t.doc_type));
                    cards.forEach((c) => grouped.add(c.id));
                    if (cards.length === 0) return null;
                    return (
                        <div key={label} className="thq-subsection">
                            <div className="thq-subsection-header">{label}</div>
                            {renderTemplateCollection(cards)}
                        </div>
                    );
                })}
                {(() => {
                    const rest = list.filter((t) => !grouped.has(t.id));
                    return rest.length ? renderTemplateCollection(rest) : null;
                })()}
            </div>
        );
    };

    return (
        <Layout showLeftNavSidebar showRightSidebar={false}>
            <div className="thq-hub">
                {/* Shared screen header: brand block + pill tray, no right widgets —
                    the component's empty actions zone balances the tray on centre. */}
                <HMSPageHeader
                    title="Template Headquarters"
                    kicker="Workspace"
                    tabs={{ items: thqTabs, activeKey: activeTab, onChange: setActiveTab }}
                />

                {/* Summary strip */}
                <div className="thq-stats-row">
                    <div className="thq-stat-card thq-stat-templates">
                        <div className="thq-stat-icon"><FontAwesomeIcon icon={faLayerGroup} /></div>
                        <div>
                            <div className="thq-stat-value">{loading ? '…' : templates.length}</div>
                            <div className="thq-stat-label">Templates</div>
                        </div>
                    </div>
                    <div className="thq-stat-card thq-stat-blocks">
                        <div className="thq-stat-icon"><FontAwesomeIcon icon={faStamp} /></div>
                        <div>
                            <div className="thq-stat-value">{loading ? '…' : allBlocks.length}</div>
                            <div className="thq-stat-label">Content Blocks</div>
                        </div>
                    </div>
                    <div className="thq-stat-card thq-stat-legal">
                        <div className="thq-stat-icon"><FontAwesomeIcon icon={faScaleBalanced} /></div>
                        <div>
                            <div className="thq-stat-value">
                                {loading ? '…' : allBlocks.filter((b) => b.requires_approval).length}
                            </div>
                            <div className="thq-stat-label">Legal Blocks</div>
                        </div>
                    </div>
                    <div className="thq-stat-card thq-stat-pending">
                        <div className="thq-stat-icon"><FontAwesomeIcon icon={faInbox} /></div>
                        <div>
                            <div className="thq-stat-value">{loading ? '…' : pending.length}</div>
                            <div className="thq-stat-label">Awaiting You</div>
                        </div>
                    </div>
                </div>

                <div className="thq-content">
                    {loading ? (
                        /* Three blocks in the real cabinet grid, at roughly the
                           height a template card settles on, so the grid does
                           not jump when the templates land. */
                        <div className="thq-tpl-grid">
                            <HMSSkeleton shape="rect" height={190} />
                            <HMSSkeleton shape="rect" height={190} />
                            <HMSSkeleton shape="rect" height={190} />
                        </div>
                    ) : error ? (
                        <div className="thq-card">
                            <div className="thq-form-error">
                                <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
                            </div>
                        </div>
                    ) : activeTab === 'approvals' ? (
                        <ApprovalsTab
                            pending={pending}
                            blocksById={blocksById}
                            onRefresh={() => loadData({ silent: true })}
                            onPreview={openPreview}
                            previewableDocTypes={PREVIEWABLE_DOC_TYPES}
                        />
                    ) : (
                        <>
                            <div className="thq-toolbar">
                                <div className="thq-toolbar-left">
                                    <div className="thq-search">
                                        <FontAwesomeIcon icon={faMagnifyingGlass} className="thq-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search templates…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="thq-toolbar-right">
                                    <button
                                        type="button"
                                        className="thq-view-toggle"
                                        onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                                        title={viewMode === 'grid' ? 'List view' : 'Grid view'}
                                    >
                                        <FontAwesomeIcon icon={viewMode === 'grid' ? faList : faTh} />
                                    </button>
                                </div>
                            </div>

                            {filteredTemplates.length === 0 ? (
                                /* No `variant` — the `.thq-card` around it is
                                   already the surface. */
                                <div className="thq-card">
                                    <HMSEmptyState
                                        icon={<FontAwesomeIcon icon={faLayerGroup} />}
                                        title="No templates found"
                                        hint={templates.length === 0
                                            ? 'No document templates have been registered yet.'
                                            : search.trim()
                                                ? 'No templates match your search in this category.'
                                                : 'No templates in this category yet.'}
                                    />
                                </div>
                            ) : activeTab === 'deals' ? (
                                renderDealsGroups(filteredTemplates)
                            ) : (
                                renderTemplateCollection(filteredTemplates)
                            )}
                        </>
                    )}
                </div>

                {/* Template detail drawer */}
                {selectedTemplate && (
                    <div className="thq-drawer-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedTemplate(null); }}>
                        <aside className="thq-drawer" role="dialog" aria-modal="true">
                            <div className="thq-drawer-head">
                                <div>
                                    <div className="thq-drawer-eyebrow">
                                        <span className="thq-module-chip">{selectedTemplate.module_display}</span>
                                        <code className="thq-tpl-doctype">{selectedTemplate.doc_type}</code>
                                    </div>
                                    <h2 className="thq-drawer-title">{selectedTemplate.name}</h2>
                                    <div className="thq-drawer-sub">
                                        <FontAwesomeIcon icon={faBuilding} />
                                        {selectedTemplate.hms_entity_name || 'All entities'}
                                    </div>
                                </div>
                                <button type="button" className="thq-icon-btn" onClick={() => setSelectedTemplate(null)} aria-label="Close">
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>

                            {selectedTemplate.description ? (
                                <p className="thq-drawer-desc">{selectedTemplate.description}</p>
                            ) : null}

                            {(() => {
                                const isInvoiceFamily = INVOICE_FAMILY_DOC_TYPES.has(selectedTemplate.doc_type);
                                const drawerPreview = getTemplatePreview(selectedTemplate, invoiceVariant);
                                const drawerBusy = drawerPreview && previewLoading === drawerPreview.key;
                                if (!selectedTemplate.master_file && !drawerPreview) return null;
                                return (
                                    <>
                                        {drawerPreview && isInvoiceFamily ? (
                                            <div className="thq-variant-controls">
                                                <label className="thq-variant-field">
                                                    <span className="thq-variant-label">Template</span>
                                                    <select
                                                        className="thq-variant-select"
                                                        value={invoiceVariant.template_type}
                                                        onChange={(e) => setInvoiceVariant((v) => ({ ...v, template_type: e.target.value }))}
                                                    >
                                                        <option value="solids_eng">Solids EN</option>
                                                        <option value="solids_de">Solids DE</option>
                                                        <option value="fuels_eng">Fuels EN</option>
                                                    </select>
                                                </label>
                                                <label className="thq-variant-field">
                                                    <span className="thq-variant-label">Format</span>
                                                    <select
                                                        className="thq-variant-select"
                                                        value={invoiceVariant.number_format}
                                                        onChange={(e) => setInvoiceVariant((v) => ({ ...v, number_format: e.target.value }))}
                                                    >
                                                        <option value="US">US</option>
                                                        <option value="EU">EU</option>
                                                    </select>
                                                </label>
                                            </div>
                                        ) : null}
                                        <div className="thq-drawer-actions">
                                            {selectedTemplate.master_file ? (
                                                <HMSButton variant="ghost" size="compact" onClick={() => openMaster(selectedTemplate)}>
                                                    <FontAwesomeIcon icon={faEye} /> View master file
                                                </HMSButton>
                                            ) : null}
                                            {drawerPreview ? (
                                                <HMSButton
                                                    variant="ghost"
                                                    size="compact"
                                                    onClick={() => openPreview(drawerPreview.run, drawerPreview.fileName, drawerPreview.key)}
                                                    disabled={Boolean(previewLoading)}
                                                    title={drawerPreview.caption || undefined}
                                                >
                                                    <FontAwesomeIcon icon={drawerBusy ? faSpinner : faFilePdf} spin={drawerBusy} />
                                                    {drawerBusy ? 'Preparing…' : drawerPreview.label}
                                                </HMSButton>
                                            ) : null}
                                        </div>
                                    </>
                                );
                            })()}

                            <div className="thq-drawer-body">
                                {groupedBlocks.length === 0 ? (
                                    /* No icon: the block this replaces carried
                                       none, only the title/body pair. */
                                    <HMSEmptyState
                                        title="No editable content"
                                        hint="This template has no content blocks registered yet."
                                    />
                                ) : (
                                    groupedBlocks.map(([categoryName, blocks]) => (
                                        <div key={categoryName} className="thq-block-group">
                                            <div className="thq-block-group-title">{categoryName}</div>
                                            {blocks.map((block) => {
                                                const status = blockStatus(block);
                                                const isPending = Boolean(block.pending_version);
                                                return (
                                                    <div key={block.id} className="thq-block-row">
                                                        <div className="thq-block-row-main">
                                                            <div className="thq-block-row-label">
                                                                {block.label}
                                                                {block.requires_approval && (
                                                                    <span className="thq-legal-badge">
                                                                        <FontAwesomeIcon icon={faScaleBalanced} />
                                                                        Legal · approval required
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {block.description ? (
                                                                <div className="thq-block-row-desc">{block.description}</div>
                                                            ) : null}
                                                            <div className="thq-block-row-status">
                                                                <HMSBadge status={status.status}>
                                                                    {status.status === 'approved'
                                                                        ? <><FontAwesomeIcon icon={faCircleCheck} /> {status.label}</>
                                                                        : status.label}
                                                                </HMSBadge>
                                                                <span className="thq-block-format">{block.format_display}</span>
                                                            </div>
                                                        </div>
                                                        {/* Edit is the shared blue-gradient action; the read-only
                                                            View state is an amber outline the primitive has no
                                                            variant for, so it keeps its local class. */}
                                                        {isPending ? (
                                                            <button
                                                                type="button"
                                                                className="thq-block-view-btn"
                                                                onClick={() => setEditingBlock(block)}
                                                                title="A change is awaiting approval — view only"
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                                View
                                                            </button>
                                                        ) : (
                                                            <HMSButton
                                                                variant="saveDraft"
                                                                size="compact"
                                                                onClick={() => setEditingBlock(block)}
                                                                title="Edit content"
                                                            >
                                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                                Edit
                                                            </HMSButton>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))
                                )}
                            </div>
                        </aside>
                    </div>
                )}

                {editingBlock && (
                    <BlockEditorModal
                        block={editingBlock}
                        onClose={() => setEditingBlock(null)}
                        onSaved={() => loadData({ silent: true })}
                        onPreview={openPreview}
                        canPreview={PREVIEWABLE_DOC_TYPES.has(editingBlock.doc_type)}
                    />
                )}

                {/* Master-file preview — also lifted above the drawer (1500) so
                    "View master file" launched from the drawer renders on top. */}
                <div className="thq-preview-host">
                    <PdfPreviewModal
                        isOpen={Boolean(previewFile)}
                        pdfUrl={previewFile?.url}
                        fileName={previewFile?.name}
                        onClose={() => setPreviewFile(null)}
                    />
                </div>

                {/* Single reusable preview host. The wrapper lifts it into its own
                    stacking context above the drawer (1500) and editor modal (2000)
                    so a preview launched from either still renders on top. */}
                <div className="thq-preview-host">
                    <PdfPreviewModal
                        isOpen={previewOpen}
                        pdfUrl={previewUrl}
                        fileName={previewTitle}
                        onClose={closePreview}
                    />
                </div>

                <Footer />
            </div>
        </Layout>
    );
};

export default withAuth(TemplateHeadquarters);
